import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Grid, 
  Typography, 
  FormControl, 
  InputLabel, 
  Select, 
  MenuItem,
  Card,
  CardMedia,
  CardContent,
  CardActions,
  Button,
  CircularProgress,
  Snackbar,
  Alert
} from '@mui/material';
import { useTranslation } from '../hooks/useTranslation';
import { Tutorial } from '../types/tutorial';
import api from '../utils/api';
import { CheckCircle as CheckCircleIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { ProductType, useCheckout } from '../contexts/CheckoutContext';
import { useAuth } from '../contexts/AuthContext';
import { useLocation } from 'react-router-dom';

export const TutorialsPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const { addToCheckout } = useCheckout();
  const [tutorials, setTutorials] = useState<Tutorial[]>([]);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState('newest');
  const [difficulty, setDifficulty] = useState('all');
  const [duration, setDuration] = useState('all');
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success'
  });
  const location = useLocation();

  useEffect(() => {
    fetchTutorials();
  }, []);

  const fetchTutorials = async () => {
    try {
      setLoading(true);
      const response = await api.get<{ data: Tutorial[] }>('/tutorials', {
        params: {
          sortBy,
          difficulty: difficulty !== 'all' ? difficulty : undefined,
          duration: duration !== 'all' ? duration : undefined
        }
      });
      setTutorials(response.data.data);
    } catch (error) {
      console.error('Error fetching tutorials:', error);
      setSnackbar({
        open: true,
        message: t('errors.fetchTutorials'),
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSortChange = (event: any) => {
    setSortBy(event.target.value);
  };

  const handleDifficultyChange = (event: any) => {
    setDifficulty(event.target.value);
  };

  const handleDurationChange = (event: any) => {
    setDuration(event.target.value);
  };

  const handleTutorialClick = async (tutorial: Tutorial) => {
    if (!isAuthenticated) {
      navigate('/login', { state: { from: location } });
      return;
    }

    if (tutorial.price === 0) {
      navigate(`/tutorials/${tutorial._id}`);
    } else {
      try {
        await addToCheckout(tutorial._id, ProductType.TUTORIAL);
        setSnackbar({
          open: true,
          message: t('pages.tutorials.addedToCheckout'),
          severity: 'success'
        });
      } catch (error) {
        console.error('Error adding to checkout:', error);
        setSnackbar({
          open: true,
          message: t('errors.addToCheckout'),
          severity: 'error'
        });
      }
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        {t('pages.tutorials.title')}
      </Typography>
      <Typography variant="subtitle1" color="text.secondary" paragraph>
        {t('pages.tutorials.subtitle')}
      </Typography>

      <Box sx={{ mb: 4 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <FormControl fullWidth>
              <InputLabel>{t('pages.tutorials.filters.sortBy')}</InputLabel>
              <Select 
                value={sortBy} 
                label={t('pages.tutorials.filters.sortBy')}
                onChange={handleSortChange}
              >
                <MenuItem value="newest">{t('pages.tutorials.filters.sortBy')}</MenuItem>
                <MenuItem value="popular">Most Popular</MenuItem>
                <MenuItem value="rating">Highest Rated</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={4}>
            <FormControl fullWidth>
              <InputLabel>{t('pages.tutorials.filters.difficulty')}</InputLabel>
              <Select 
                value={difficulty} 
                label={t('pages.tutorials.filters.difficulty')}
                onChange={handleDifficultyChange}
              >
                <MenuItem value="all">{t('pages.tutorials.categories.all')}</MenuItem>
                <MenuItem value="beginner">{t('pages.tutorials.categories.beginner')}</MenuItem>
                <MenuItem value="intermediate">{t('pages.tutorials.categories.intermediate')}</MenuItem>
                <MenuItem value="advanced">{t('pages.tutorials.categories.advanced')}</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={4}>
            <FormControl fullWidth>
              <InputLabel>{t('pages.tutorials.filters.duration')}</InputLabel>
              <Select 
                value={duration} 
                label={t('pages.tutorials.filters.duration')}
                onChange={handleDurationChange}
              >
                <MenuItem value="all">Any Duration</MenuItem>
                <MenuItem value="short">Under 15 minutes</MenuItem>
                <MenuItem value="medium">15-30 minutes</MenuItem>
                <MenuItem value="long">Over 30 minutes</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Grid container spacing={3}>
          {tutorials.map((tutorial) => (
            <Grid item xs={12} sm={6} md={4} key={tutorial._id}>
              <Card sx={{ 
                height: '100%', 
                display: 'flex', 
                flexDirection: 'column',
                transition: 'transform 0.2s',
                '&:hover': {
                  transform: 'scale(1.02)',
                }
              }}>
                <CardMedia
                  component="img"
                  height="200"
                  image={tutorial.imagePath}
                  alt={tutorial.title}
                  sx={{ objectFit: 'cover' }}
                />
                <CardContent sx={{ flexGrow: 1 }}>
                  <Typography gutterBottom variant="h6" component="div">
                    {tutorial.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {tutorial.description}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    {tutorial.price === 0 ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', color: 'success.main' }}>
                        <CheckCircleIcon sx={{ mr: 1 }} />
                        <Typography variant="body2">{t('pages.tutorials.available')}</Typography>
                      </Box>
                    ) : (
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                        {tutorial.discount > 0 ? (
                          <>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography variant="h6" color="primary">
                                ₪{Math.round(tutorial.price * (1 - tutorial.discount / 100))}
                              </Typography>
                              <Typography variant="body2" sx={{ textDecoration: 'line-through', color: 'text.secondary' }}>
                                ₪{tutorial.price}
                              </Typography>
                              <Typography variant="body2" color="error.main">
                                -{tutorial.discount}%
                              </Typography>
                            </Box>
                          </>
                        ) : (
                          <Typography variant="h6" color="primary">
                            ₪{tutorial.price}
                          </Typography>
                        )}
                      </Box>
                    )}
                  </Box>
                </CardContent>
                <CardActions>
                  <Button 
                    size="small" 
                    color="primary" 
                    fullWidth
                    onClick={() => handleTutorialClick(tutorial)}
                  >
                    {tutorial.price === 0 ? t('pages.tutorials.openTutorial') : t('pages.tutorials.addToCheckout')}
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}; 